// Unified company service (for backward compatibility and convenience)
import { companyListService } from './services/companyList.service';
import { companyProfileService } from './services/companyProfile.service';
import { companyUpdateService } from './services/companyUpdate.service';
import { companyLogoService } from './services/companyLogo.service';
import { companyCreate } from './services/companyCreate.service';
import { geographyService, type Region, type Commune } from '../geography';
import type { ApiResponse } from '@/api/shared';
import type {
  CompanyListParams,
  CompaniesPage,
} from './types/companyList.types';
import type { CompanyProfile } from './types/companyProfile.types';
import type { LC_CreateCompanyIn, LC_CreateCompanyOut } from './types/companyCreate.types';
import type { UpdateCompanyLogoResult } from './types/companyLogo.types';

/**
 * Unified CompanyService that delegates to modular services
 * Maintains backward compatibility with existing code
 */
export class CompanyService {
  // List operations
  async list(params: CompanyListParams, signal?: AbortSignal): Promise<ApiResponse<CompaniesPage>> {
    return companyListService.list(params, signal);
  }

  // Profile operations
  async getById(companyId: number): Promise<ApiResponse<CompanyProfile>> {
    return companyProfileService.getById(companyId);
  }

  // Update operations
  async updateInfo(companyId: number, profile: CompanyProfile): Promise<ApiResponse<void>> {
    return companyUpdateService.updateInfo(companyId, profile);
  }

  async updateConfig(companyId: number, profile: CompanyProfile): Promise<ApiResponse<void>> {
    return companyUpdateService.updateConfig(companyId, profile);
  }

  // Create operations
  async create(input: LC_CreateCompanyIn): Promise<ApiResponse<LC_CreateCompanyOut>> {
    return companyCreate.create(input);
  }

  // Logo operations
  async updateLogo(companyId: number, logoDataUrl: string): Promise<UpdateCompanyLogoResult> {
    return companyLogoService.updateLogo(companyId, logoDataUrl);
  }

  // Geography operations
  async getRegions(country: string): Promise<ApiResponse<Region[]>> {
    return geographyService.getRegions(country);
  }

  async getCommunes(country: string, regionId: number): Promise<ApiResponse<Commune[]>> {
    return geographyService.getCommunes(country, regionId);
  }
}

export const companyService = new CompanyService();
